import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const AddComplaintForm = () => {
  // Get today's date in YYYY-MM-DD format for default values
  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };
  const [inputs, setInputs] = useState({
    ComplaintTypeId: "",
    FarmerName: "",
    FarmerVillageName: "",
    FarmerAddress: "",
    FarmerNumber: "",
    FarmGatNumberSurveyNumberDetails: "",
    FarmLatitude: "",
    FarmLongitude: "",
    Taluka: "",
    District: "",
    State: "",
    LocationLink: "",
    ComplaintDetails1: "",
    ComplaintDetails2: "",
    SowingDate: getTodayDate(),
    IssueSpottedDate: getTodayDate(),
    IssueReportedDate: getTodayDate(),
    Status: "Pending",
  });

  // Predefined states for dropdown
  const states = ["Maharashtra", "Telangana", "Goa", "Karnataka", "Gujarat"];

  // Single upload mode states
  const [documentSections, setDocumentSections] = useState(1);
  const [singleFiles, setSingleFiles] = useState([null]);
  const [singleFileNames, setSingleFileNames] = useState([""]);
  const [singleDocumentDetails, setSingleDocumentDetails] = useState([""]);
  const [singleDocumentComments, setSingleDocumentComments] = useState([""]);

  // Bulk upload mode states
  const [isDragOver, setIsDragOver] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkDocumentDetails, setBulkDocumentDetails] = useState([]);
  const [bulkDocumentComments, setBulkDocumentComments] = useState([]);

  const [uploadMode, setUploadMode] = useState("single"); // 'single' or 'bulk'
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingTypes, setFetchingTypes] = useState(true);
  const navigate = useNavigate();

  // Get distributor ID and handle -1 case
  const getDistributorId = () => {
    const distID = localStorage.getItem("distributorId");
    // If distributor ID is -1 or empty, return null
    if (
      distID === "-1" ||
      !distID ||
      distID === "null" ||
      distID === "undefined"
    ) {
      return null;
    }
    return distID;
  };

  // Fetch complaint types on component mount
  useEffect(() => {
    fetchComplaintTypes();
  }, []);

  const fetchComplaintTypes = async () => {
    try {
      setFetchingTypes(true);
      console.log("Fetching complaint types from API...");

      const response = await axios.get(
        "https://erelatevmdev.centralindia.cloudapp.azure.com/api/Complaint/GetComplaintTypes/GetComplaintTypes",
        {
          headers: {
            "Cache-Control": "no-cache",
            "Access-Control-Allow-Origin": "*",
            Authorization: localStorage.getItem("loginToken") || "",
          },
        }
      );

      console.log("API Response:", response);
      console.log("Response Data:", response.data);

      if (response.data && response.data.isSuccess) {
        if (response.data.result && Array.isArray(response.data.result)) {
          console.log("Setting complaint types from response.data.result");
          setComplaintTypes(response.data.result);
        } else {
          console.error("Unexpected result format:", response.data.result);
          toast.error("Unexpected data format from server");
        }
      } else {
        toast.error("Failed to load complaint types");
      }
    } catch (error) {
      console.error("Error fetching complaint types:", error);
      if (error.response) {
        console.error("Error response:", error.response);
        if (error.response.status === 401) {
          toast.error("Session expired. Please login again.");
          navigate("/login");
        } else {
          toast.error(`Server error: ${error.response.status}`);
        }
      } else {
        toast.error("Network error. Please check your connection.");
      }
    } finally {
      setFetchingTypes(false);
    }
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  // Single upload mode functions
  const addDocumentSection = () => {
    if (documentSections < 10) {
      setDocumentSections((prev) => prev + 1);
      setSingleFiles((prev) => [...prev, null]);
      setSingleFileNames((prev) => [...prev, ""]);
      setSingleDocumentDetails((prev) => [...prev, ""]);
      setSingleDocumentComments((prev) => [...prev, ""]);
    } else {
      toast.info("Maximum 10 documents allowed");
    }
  };

  const removeDocumentSection = (index) => {
    if (documentSections > 1) {
      setDocumentSections((prev) => prev - 1);
      setSingleFiles((prev) => prev.filter((_, i) => i !== index));
      setSingleFileNames((prev) => prev.filter((_, i) => i !== index));
      setSingleDocumentDetails((prev) => prev.filter((_, i) => i !== index));
      setSingleDocumentComments((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSingleFileChange = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const newFiles = [...singleFiles];
      newFiles[index] = file;
      setSingleFiles(newFiles);

      const newFileNames = [...singleFileNames];
      newFileNames[index] = file.name;
      setSingleFileNames(newFileNames);
    }
  };

  const handleSingleDetailChange = (index, value) => {
    const newDetails = [...singleDocumentDetails];
    newDetails[index] = value;
    setSingleDocumentDetails(newDetails);
  };

  const handleSingleCommentChange = (index, value) => {
    const newComments = [...singleDocumentComments];
    newComments[index] = value;
    setSingleDocumentComments(newComments);
  };

  // Bulk upload mode functions
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.slice(0, 10);

    if (validFiles.length > 0) {
      setBulkFiles(validFiles);

      // Initialize details and comments arrays
      const newDetails = validFiles.map(
        (file) => `Uploaded: ${file.name} (${new Date().toLocaleDateString()})`
      );
      const newComments = validFiles.map(() => "");

      setBulkDocumentDetails(newDetails);
      setBulkDocumentComments(newComments);
    }
  };

  const handleBulkFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).slice(0, 10);

    if (selectedFiles.length > 0) {
      setBulkFiles(selectedFiles);

      // Initialize details and comments arrays
      const newDetails = selectedFiles.map(
        (file) => `Uploaded: ${file.name} (${new Date().toLocaleDateString()})`
      );
      const newComments = selectedFiles.map(() => "");

      setBulkDocumentDetails(newDetails);
      setBulkDocumentComments(newComments);
    }
  };

  const removeBulkFile = (index) => {
    setBulkFiles((prev) => prev.filter((_, i) => i !== index));
    setBulkDocumentDetails((prev) => prev.filter((_, i) => i !== index));
    setBulkDocumentComments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBulkDetailChange = (index, value) => {
    const newDetails = [...bulkDocumentDetails];
    newDetails[index] = value;
    setBulkDocumentDetails(newDetails);
  };

  const handleBulkCommentChange = (index, value) => {
    const newComments = [...bulkDocumentComments];
    newComments[index] = value;
    setBulkDocumentComments(newComments);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();

      // Add all complaint data to FormData
      Object.keys(inputs).forEach((key) => {
        if (
          inputs[key] !== null &&
          inputs[key] !== undefined &&
          inputs[key] !== ""
        ) {
          formData.append(key, inputs[key]);
        }
      });

      // Add distributor and user IDs with proper handling for -1 case
      const distributorId = getDistributorId();
      if (distributorId) {
        formData.append("DistributorId", distributorId);
      } else {
        formData.append("DistributorId", ""); // Send empty string instead of null
      }

      formData.append("CreatedById", localStorage.getItem("userId") || "");

      // Add files and metadata based on upload mode
      if (uploadMode === "single") {
        singleFiles.forEach((file, index) => {
          if (file) {
            formData.append("Files", file);
            if (singleDocumentDetails[index]) {
              formData.append(
                `DocumentDetails[${index}]`,
                singleDocumentDetails[index]
              );
            }
            if (singleDocumentComments[index]) {
              formData.append(
                `DocumentComments[${index}]`,
                singleDocumentComments[index]
              );
            }
          }
        });
      } else {
        bulkFiles.forEach((file, index) => {
          if (file) {
            formData.append("Files", file);
            if (bulkDocumentDetails[index]) {
              formData.append(
                `DocumentDetails[${index}]`,
                bulkDocumentDetails[index]
              );
            }
            if (bulkDocumentComments[index]) {
              formData.append(
                `DocumentComments[${index}]`,
                bulkDocumentComments[index]
              );
            }
          }
        });
      }

      console.log(
        "Submitting complaint with files:",
        uploadMode === "single"
          ? singleFiles.filter((file) => file !== null).length
          : bulkFiles.length
      );

      console.log("Distributor ID being sent:", distributorId);

      const response = await axios.post(
        "https://erelatevmdev.centralindia.cloudapp.azure.com/api/Complaint/SubmitComplaint/SubmitComplaint",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "Cache-Control": "no-cache",
            "Access-Control-Allow-Origin": "*",
            Authorization: localStorage.getItem("loginToken") || "",
          },
        }
      );

      console.log("API Response:", response);

      if (response.status === 200) {
        if (response.data && response.data.isSuccess) {
          toast.success("Complaint submitted successfully!");
          // Reset form
          setInputs({
            ComplaintTypeId: "",
            FarmerName: "",
            FarmerVillageName: "",
            FarmerAddress: "",
            FarmerNumber: "",
            FarmGatNumberSurveyNumberDetails: "",
            FarmLatitude: "",
            FarmLongitude: "",
            Taluka: "",
            District: "",
            State: "",
            LocationLink: "",
            ComplaintDetails1: "",
            ComplaintDetails2: "",
            SowingDate: getTodayDate(),
            IssueSpottedDate: getTodayDate(),
            IssueReportedDate: getTodayDate(),
            Status: "Pending",
          });

          // Reset upload sections based on mode
          if (uploadMode === "single") {
            setDocumentSections(1);
            setSingleFiles([null]);
            setSingleFileNames([""]);
            setSingleDocumentDetails([""]);
            setSingleDocumentComments([""]);
          } else {
            setBulkFiles([]);
            setBulkDocumentDetails([]);
            setBulkDocumentComments([]);
          }

          // Clear file inputs
          document.querySelectorAll('input[type="file"]').forEach((input) => {
            input.value = "";
          });

          // Navigate to complaints list after successful submission
          setTimeout(() => {
            navigate("/AddComplaintForm");
          }, 2000);
        } else {
          toast.error(response.data?.message || "Failed to submit complaint");
        }
      }
    } catch (error) {
      console.error("Error submitting complaint:", error);
      if (error.response) {
        console.error("Error response:", error.response);
        if (error.response.status === 401) {
          toast.error("Session expired. Please login again.");
          navigate("/login");
        } else if (error.response.status === 404) {
          toast.error("API endpoint not found. Please check the URL.");
        } else if (error.response.data) {
          toast.error(
            error.response.data.message || "Error submitting complaint"
          );
        }
      } else {
        toast.error("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Render single document upload section
  const renderSingleDocumentSection = (index) => (
    <div className="col-sm-12 mt-3" key={index}>
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5>Document {index + 1}</h5>
          {documentSections > 1 && (
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => removeDocumentSection(index)}
            >
              Remove
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-sm-4">
              <div className="form-group">
                <label className="form-label">Upload File</label>
                <div className="file-drop-area">
                  <span className="choose-file-button">Choose file</span>
                  <span className="file-message">
                    {singleFileNames[index] || "or drag and drop file here"}
                  </span>
                  <input
                    className="file-input"
                    type="file"
                    onChange={(e) => handleSingleFileChange(index, e)}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                </div>
              </div>
            </div>

            <div className="col-sm-4">
              <div className="form-group">
                <label className="form-label">Document Details</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter document details"
                  value={singleDocumentDetails[index]}
                  onChange={(e) =>
                    handleSingleDetailChange(index, e.target.value)
                  }
                />
              </div>
            </div>

            <div className="col-sm-4">
              <div className="form-group">
                <label className="form-label">Comments</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter comments"
                  value={singleDocumentComments[index]}
                  onChange={(e) =>
                    handleSingleCommentChange(index, e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render bulk upload section
  const renderBulkUploadSection = () => (
    <>
      <div className="col-sm-12 mt-3">
        <div
          className={`p-4 border-2 ${
            isDragOver ? "border-primary bg-light" : "border-dashed"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            borderRadius: "10px",
            cursor: "pointer",
            border: "2px dashed #ccc",
          }}
        >
          <div className="text-center">
            <i className="fas fa-cloud-upload-alt fa-3x text-primary mb-3"></i>
            <h5>Drag & Drop Files Here</h5>
            <p className="text-muted">Upload up to 10 documents at once</p>
            <p className="text-muted">or</p>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => document.getElementById("bulkFileInput").click()}
            >
              Browse Files
            </button>
            <input
              id="bulkFileInput"
              type="file"
              multiple
              onChange={handleBulkFileSelect}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>

      {/* Show selected files in bulk mode */}
      {bulkFiles.length > 0 && (
        <div className="col-sm-12 mt-3">
          <div className="card">
            <div className="card-header">
              <h5>Selected Files ({bulkFiles.length}/10)</h5>
            </div>
            <div className="card-body">
              {bulkFiles.map((file, index) => (
                <div key={index} className="mb-3 p-3 border rounded">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">{file.name}</h6>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeBulkFile(index)}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label className="form-label">Details</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter document details"
                          value={bulkDocumentDetails[index]}
                          onChange={(e) =>
                            handleBulkDetailChange(index, e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label className="form-label">Comments</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter comments"
                          value={bulkDocumentComments[index]}
                          onChange={(e) =>
                            handleBulkCommentChange(index, e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="col-12">
        <div className="card">
          <div className="card-header bg-gray">
            <h3 className="card-title">Register New Complaint</h3>
          </div>

          <div className="card-body nobornopad">
            <form onSubmit={handleSubmit} className="master-form" noValidate>
              <div className="row card-body">
                {/* Complaint Type */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label
                      htmlFor="ComplaintTypeId"
                      className="form-label mandate"
                    >
                      Complaint Type
                    </label>
                    <select
                      className="form-control"
                      name="ComplaintTypeId"
                      required
                      value={inputs.ComplaintTypeId}
                      onChange={handleChange}
                      disabled={fetchingTypes}
                    >
                      <option value="">Select Complaint Type</option>
                      {complaintTypes.map((type) => {
                        const id = type.id || type.Id || type.value;
                        const name =
                          type.name || type.Name || type.text || type.Text;
                        return (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        );
                      })}
                    </select>
                    {fetchingTypes ? (
                      <div className="mt-1 text-info">
                        <small>Loading complaint types...</small>
                      </div>
                    ) : complaintTypes.length === 0 ? (
                      <div className="mt-1 text-danger">
                        <small>
                          No complaint types available. Please refresh.
                        </small>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Farmer Name */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="FarmerName" className="form-label mandate">
                      Farmer Name
                    </label>
                    <input
                      type="text"
                      name="FarmerName"
                      className="form-control"
                      placeholder="Enter Farmer Name"
                      value={inputs.FarmerName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="FarmerAddress" className="form-label">
                      Address
                    </label>
                    <input
                      type="text"
                      name="FarmerAddress"
                      className="form-control"
                      placeholder="Enter Address"
                      value={inputs.FarmerAddress}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Village Name */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="FarmerVillageName" className="form-label">
                      Village Name
                    </label>
                    <input
                      type="text"
                      name="FarmerVillageName"
                      className="form-control"
                      placeholder="Enter Village Name"
                      value={inputs.FarmerVillageName}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="Taluka" className="form-label">
                      Taluka
                    </label>
                    <input
                      type="text"
                      name="Taluka"
                      className="form-control"
                      placeholder="Enter Taluka"
                      value={inputs.Taluka}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* District */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="District" className="form-label">
                      District
                    </label>
                    <input
                      type="text"
                      name="District"
                      className="form-control"
                      placeholder="Enter District"
                      value={inputs.District}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* State - Changed from input to dropdown */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="State" className="form-label">
                      State
                    </label>
                    <select
                      className="form-control"
                      name="State"
                      value={inputs.State}
                      onChange={handleChange}
                    >
                      <option value="">Select State</option>
                      {states.map((state, index) => (
                        <option key={index} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Location Link */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="LocationLink" className="form-label">
                      Location Link
                    </label>
                    <input
                      type="url"
                      name="LocationLink"
                      className="form-control"
                      placeholder="Enter Google Maps or other location link"
                      value={inputs.LocationLink}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label
                      htmlFor="FarmerNumber"
                      className="form-label mandate"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="FarmerNumber"
                      className="form-control"
                      placeholder="Enter Phone Number"
                      value={inputs.FarmerNumber}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                    />
                  </div>
                </div>

                {/* Survey Number */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label
                      htmlFor="FarmGatNumberSurveyNumberDetails"
                      className="form-label"
                    >
                      Survey Number / Gat Number
                    </label>
                    <input
                      type="text"
                      name="FarmGatNumberSurveyNumberDetails"
                      className="form-control"
                      placeholder="Enter Survey/Gat Number"
                      value={inputs.FarmGatNumberSurveyNumberDetails}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Coordinates */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="FarmLatitude" className="form-label">
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      name="FarmLatitude"
                      className="form-control"
                      placeholder="Enter Latitude"
                      value={inputs.FarmLatitude}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="FarmLongitude" className="form-label">
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      name="FarmLongitude"
                      className="form-control"
                      placeholder="Enter Longitude"
                      value={inputs.FarmLongitude}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Complaint Details */}
                <div className="col-sm-12">
                  <div className="form-group">
                    <label
                      htmlFor="ComplaintDetails1"
                      className="form-label mandate"
                    >
                      Complaint Details
                    </label>
                    <textarea
                      name="ComplaintDetails1"
                      className="form-control"
                      placeholder="Describe your complaint in detail..."
                      value={inputs.ComplaintDetails1}
                      onChange={handleChange}
                      required
                      rows="4"
                    />
                  </div>
                </div>

                <div className="col-sm-12">
                  <div className="form-group">
                    <label htmlFor="ComplaintDetails2" className="form-label">
                      Additional Details
                    </label>
                    <textarea
                      name="ComplaintDetails2"
                      className="form-control"
                      placeholder="Any additional information..."
                      value={inputs.ComplaintDetails2}
                      onChange={handleChange}
                      rows="3"
                    />
                  </div>
                </div>

                {/* Dates */}
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="SowingDate" className="form-label">
                      Sowing Date
                    </label>
                    <input
                      type="date"
                      name="SowingDate"
                      className="form-control"
                      value={inputs.SowingDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="IssueSpottedDate" className="form-label">
                      Issue Spotted Date
                    </label>
                    <input
                      type="date"
                      name="IssueSpottedDate"
                      className="form-control"
                      value={inputs.IssueSpottedDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="IssueReportedDate" className="form-label">
                      Issue Reported Date
                    </label>
                    <input
                      type="date"
                      name="IssueReportedDate"
                      className="form-control"
                      value={inputs.IssueReportedDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Document Upload Mode Selector */}
                <div className="col-sm-12 mt-4">
                  <div className="card">
                    <div className="card-header">
                      <h4>Upload Supporting Documents</h4>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-sm-12 mb-3">
                          <div className="btn-group" role="group">
                            <button
                              type="button"
                              className={`btn ${
                                uploadMode === "single"
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                              }`}
                              onClick={() => setUploadMode("single")}
                            >
                              Single File Upload
                            </button>
                            <button
                              type="button"
                              className={`btn ${
                                uploadMode === "bulk"
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                              }`}
                              onClick={() => setUploadMode("bulk")}
                            >
                              Bulk Upload (Max 10)
                            </button>
                          </div>
                        </div>

                        {/* Render appropriate upload section based on mode */}
                        {uploadMode === "single" ? (
                          <>
                            {Array.from({ length: documentSections }).map(
                              (_, index) => renderSingleDocumentSection(index)
                            )}
                            {documentSections < 10 && (
                              <div className="col-sm-12 mt-3">
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={addDocumentSection}
                                >
                                  + Add Another Document
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          renderBulkUploadSection()
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12 card-body">
                  <button
                    type="submit"
                    className="btn btn-success float-right submit-btn"
                    disabled={loading || fetchingTypes}
                  >
                    {loading ? "Submitting..." : "Submit Complaint"}
                  </button>
                </div>
              </div>
            </form>

            <ToastContainer
              position="bottom-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AddComplaintForm;
